import React, {
  ReactNode,
  useEffect,
  useState,
  useRef,
  CSSProperties,
  PropsWithChildren,
  useImperativeHandle,
} from 'react'

import Icon from 'components/Icon'
import Portal from 'components/Portal'

import classNames from 'helpers/css'
import useBool from 'hooks/bool'
import { isServer } from 'helpers/utilities'

import styles from './index.module.scss'

const TOOLTIP_CONTENT_GAP = 14

const calculatePositionStyles = (wrapperRectangle: DOMRect, overlayRectangle: DOMRect, placement: PlacementType) => {
  const bodyRect = document.body.getBoundingClientRect() // when scroll is disabled window.scrollY is 0, that's why body rect is taken

  const top = Math.max(wrapperRectangle.top - overlayRectangle.height + Math.abs(bodyRect.top) - TOOLTIP_CONTENT_GAP, 0)

  switch (placement) {
    case 'top': {
      return {
        left: Math.max(wrapperRectangle.left + wrapperRectangle.width / 2 - (overlayRectangle.width / 2), 0),
        top,
      }
    }

    case 'topRight': {
      return {
        left: Math.max(wrapperRectangle.left - (overlayRectangle.width - wrapperRectangle.width), 0),
        top,
      }
    }

    case 'topLeft': {
      return {
        left: Math.max(wrapperRectangle.left, 0),
        top,
      }
    }
  }
}

type NullableRefType = HTMLDivElement | null

const Tooltip = React.forwardRef<NullableRefType, PropsWithChildren<TooltipProps>>((props, ref) => {
  const {
    placement = 'top',
    size = 'medium',
    visible,
    className,
    contentClassName,
    closable,
    onClose,
    content,
    children,
    hideDelay = 100,
    onVisibleChange = (visibility) => visibility,
  } = props

  const [positionStyles, setPositionStyles] = useState<CSSProperties>({})
  const [isVisible, show, hide] = useBool(visible)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  const hideTimeout = useRef<NodeJS.Timeout>()

  const calculateTooltipPosition = () => {
    if (overlayRef.current && wrapperRef.current) {
      const wrapperRectangle = wrapperRef.current?.getBoundingClientRect()
      const overlayRectangle = overlayRef.current?.getBoundingClientRect()

      if (wrapperRectangle && overlayRectangle) {
        setPositionStyles(calculatePositionStyles(wrapperRectangle, overlayRectangle, placement))
      }
    }
  }

  const close = () => {
    hide()
    onClose && onClose()
  }

  useImperativeHandle<NullableRefType, NullableRefType>(ref, () => overlayRef.current)

  useEffect(() => {
    calculateTooltipPosition()
  }, [overlayRef.current, wrapperRef.current, isVisible, content])

  useEffect(() => {
    onVisibleChange(isVisible)
  }, [isVisible])

  useEffect(() => {
    window.addEventListener('scroll', calculateTooltipPosition)
    window.addEventListener('resize', calculateTooltipPosition)

    return () => {
      window.removeEventListener('scroll', calculateTooltipPosition)
      window.removeEventListener('resize', calculateTooltipPosition)
    }
  }, [])

  useEffect(() => {
    if (visible) {
      show()
    } else {
      hide()
    }
  }, [visible])

  return (
    <span
      className={classNames(styles, 'tooltip')}
      role="tooltip"
      ref={wrapperRef}
      onPointerEnter={() => {
        if (hideTimeout.current) clearTimeout(hideTimeout.current)
        if (visible === undefined) show()
      }}
      onPointerLeave={() => {
        hideTimeout.current = setTimeout(() => { if (visible === undefined) { hide() } }, hideDelay)
      }}
    >
      {children}
      {isVisible && !isServer() && (
        <Portal>
          <div
            className={classNames(
              styles,
              'tooltip__overlay',
              `tooltip__overlay--${size}`,
              className,
            )}
            ref={overlayRef}
            style={positionStyles}
          >
            {visible && closable && (
              <Icon
                name="close"
                className={classNames(styles, 'tooltip__overlay__content__close')}
                onClick={close}
                role="close"
              />
            )}

            <div className={classNames(styles, 'tooltip__overlay__content', contentClassName)}>
              {content}
            </div>
            <div className={classNames(
              styles,
              'tooltip__overlay__arrow',
              `tooltip__overlay__arrow__${placement}`,
            )} />
          </div>
        </Portal>
      )}
    </span>
  )
})

type PlacementType = 'top' | 'topRight' | 'topLeft'
type SizeType = 'large' | 'medium'

interface TooltipProps {
  visible?: boolean
  closable?: boolean
  placement?: PlacementType
  size?: SizeType
  content: ReactNode
  className?: string
  contentClassName?: string
  onClose?: () => void
  onVisibleChange?: (visibility: boolean) => void
  hideDelay?: number
}

export default Tooltip
